import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { adminApi, formatApiError } from '../../lib/api';
import { getPlanLabel } from '../../lib/planLabels';
import { Button } from '../../components/ui/button';
import { 
    ArrowLeft, Loader2, FileText, Brain, Briefcase, 
    UserX, UserCheck, Shield, Mail, Building, Calendar
} from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

export default function AdminUserDetailPage() {
    const { t } = useTranslation();
    const { userId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updatingRole, setUpdatingRole] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);

    useEffect(() => {
        loadData();
    }, [userId]);

    const loadData = async () => {
        try {
            const res = await adminApi.getUserDetail(userId);
            setData(res.data);
        } catch (error) {
            toast.error(t('admin_user.load_error'));
            navigate('/dashboard/admin');
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (newRole) => {
        setUpdatingRole(true);
        try {
            await adminApi.updateUserRole(userId, newRole);
            toast.success(t('admin_user.role_updated'));
            loadData();
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setUpdatingRole(false);
        }
    };

    const handleStatusToggle = async () => {
        const newStatus = data.user.status === 'active' ? 'suspended' : 'active';
        setUpdatingStatus(true);
        try {
            await adminApi.updateUserStatus(userId, newStatus);
            toast.success(newStatus === 'suspended' ? t('admin_user.user_suspended') : t('admin_user.user_reactivated'));
            loadData();
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setUpdatingStatus(false);
        }
    };

    const getStatusBadge = (status) => {
        if (status === 'suspended') {
            return <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#FEE2E2] text-[#DC2626]">{t('admin_user.status_suspended')}</span>;
        }
        return <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#D1FAE5] text-[#059669]">{t('admin_user.status_active')}</span>;
    };

    const getPlanBadge = (plan) => {
        const label = getPlanLabel(plan);
        switch (plan) {
            case 'enterprise':
                return <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#DBEAFE] text-[#002FA7]">{label}</span>;
            case 'pro':
                return <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#D1FAE5] text-[#059669]">{label}</span>;
            default:
                return <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#F3F4F6] text-[#4B5563]">{label}</span>;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
            </div>
        );
    }

    if (!data) return null;

    const { user, organization, usage, recent_activity } = data;
    const quotaPercent = usage.quota_max > 0 ? Math.round(usage.quota_used / usage.quota_max * 100) : 0;
    const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';
    const planLong = organization?.subscription_plan === 'enterprise' ? t('admin_user.enterprise_label_long')
        : organization?.subscription_plan === 'pro' ? t('admin_user.pro_label_long')
        : t('admin_user.free_label_long');

    return (
        <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-user-detail-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <button 
                        onClick={() => navigate('/dashboard/admin')}
                        className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors flex-shrink-0"
                        data-testid="back-to-admin-btn"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#4B5563]" />
                    </button>
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                            {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                <h1 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] font-['Outfit'] break-words">{user.name}</h1>
                                {getStatusBadge(user.status || 'active')}
                            </div>
                            <p className="text-[#4B5563] truncate">{user.email}</p>
                        </div>
                    </div>
                </div>
                <Button
                    variant={user.status === 'active' ? 'outline' : 'default'}
                    onClick={handleStatusToggle}
                    disabled={updatingStatus}
                    className={`w-full sm:w-auto ${user.status === 'active' 
                        ? 'border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2]' 
                        : 'bg-[#10B981] hover:bg-[#059669] text-white'}`}
                    data-testid="toggle-status-btn"
                >
                    {updatingStatus ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : user.status === 'active' ? (
                        <UserX className="w-4 h-4 mr-2" />
                    ) : (
                        <UserCheck className="w-4 h-4 mr-2" />
                    )}
                    {user.status === 'active' ? t('admin_user.btn_suspend') : t('admin_user.btn_reactivate')}
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">{t('admin_user.info_title')}</h2>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <Mail className="w-4 h-4 text-[#9CA3AF]" />
                                <div>
                                    <p className="text-xs text-[#9CA3AF]">{t('admin_user.label_email')}</p>
                                    <p className="text-sm text-[#0A0A0A]">{user.email}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Shield className="w-4 h-4 text-[#9CA3AF]" />
                                <div>
                                    <p className="text-xs text-[#9CA3AF]">{t('admin_user.label_role')}</p>
                                    <Select 
                                        value={user.role} 
                                        onValueChange={handleRoleChange}
                                        disabled={updatingRole}
                                    >
                                        <SelectTrigger className="w-[140px] h-8 text-sm mt-1" data-testid="user-role-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="admin">{t('admin.role_admin')}</SelectItem>
                                            <SelectItem value="manager">{t('admin.role_manager')}</SelectItem>
                                            <SelectItem value="recruiter">{t('admin.role_recruiter')}</SelectItem>
                                            <SelectItem value="billing_admin">{t('admin.role_billing')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Building className="w-4 h-4 text-[#9CA3AF]" />
                                <div>
                                    <p className="text-xs text-[#9CA3AF]">{t('admin_user.label_organization')}</p>
                                    <p className="text-sm text-[#0A0A0A]">{organization?.name || t('admin_user.no_org')}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 text-[#9CA3AF]" />
                                <div>
                                    <p className="text-xs text-[#9CA3AF]">{t('admin_user.label_signup')}</p>
                                    <p className="text-sm text-[#0A0A0A]">
                                        {user.created_at ? new Date(user.created_at).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">{t('admin_user.subscription_title')}</h2>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-[#9CA3AF]">{t('admin_user.current_subscription')}</span>
                                {getPlanBadge(organization?.subscription_plan || 'free')}
                            </div>
                            <div>
                                <p className="text-xs text-[#9CA3AF] mb-2">{t('admin_user.subscription_label')}</p>
                                <div className="px-3 py-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md text-sm text-[#4B5563]" data-testid="user-plan-display">
                                    {planLong}
                                </div>
                                <p className="text-[10px] text-[#9CA3AF] mt-1">{t('admin_user.managed_at_org')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">{t('admin_user.usage_title')}</h2>
                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="text-center p-4 bg-[#F9FAFB] rounded-lg">
                                <div className="flex justify-center mb-2">
                                    <FileText className="w-5 h-5 text-[#002FA7]" />
                                </div>
                                <p className="text-2xl font-bold text-[#0A0A0A]">{usage.candidates_uploaded}</p>
                                <p className="text-xs text-[#9CA3AF]">{t('admin_user.stat_cvs_imported')}</p>
                            </div>
                            <div className="text-center p-4 bg-[#F9FAFB] rounded-lg">
                                <div className="flex justify-center mb-2">
                                    <Brain className="w-5 h-5 text-[#10B981]" />
                                </div>
                                <p className="text-2xl font-bold text-[#0A0A0A]">{usage.candidates_analyzed}</p>
                                <p className="text-xs text-[#9CA3AF]">{t('admin_user.stat_ai_analyses')}</p>
                            </div>
                            <div className="text-center p-4 bg-[#F9FAFB] rounded-lg">
                                <div className="flex justify-center mb-2">
                                    <Briefcase className="w-5 h-5 text-[#F59E0B]" />
                                </div>
                                <p className="text-2xl font-bold text-[#0A0A0A]">{usage.jobs_created}</p>
                                <p className="text-xs text-[#9CA3AF]">{t('admin_user.stat_jobs_created')}</p>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-[#4B5563]">{t('admin_user.quota_label')}</span>
                                <span className={`font-medium ${quotaPercent > 80 ? 'text-[#EF4444]' : 'text-[#4B5563]'}`}>
                                    {usage.quota_used} / {usage.quota_max} ({quotaPercent}%)
                                </span>
                            </div>
                            <div className="w-full h-3 bg-[#E5E7EB] rounded-full overflow-hidden">
                                <div 
                                    className={`h-full rounded-full transition-all ${
                                        quotaPercent > 80 ? 'bg-[#EF4444]' : quotaPercent > 50 ? 'bg-[#F59E0B]' : 'bg-[#10B981]'
                                    }`}
                                    style={{ width: `${Math.min(quotaPercent, 100)}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">{t('admin_user.recent_activity')}</h2>
                        {recent_activity && recent_activity.length > 0 ? (
                            <div className="space-y-3">
                                {recent_activity.map((item, index) => (
                                    <div key={item.id || index} className="flex items-center justify-between py-2 border-b border-[#E5E7EB] last:border-0">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-2 h-2 rounded-full ${
                                                item.status === 'analyzed' ? 'bg-[#10B981]' : 
                                                item.status === 'pending' ? 'bg-[#F59E0B]' : 'bg-[#9CA3AF]'
                                            }`} />
                                            <div>
                                                <p className="text-sm text-[#0A0A0A]">{item.name || item.cv_filename}</p>
                                                <p className="text-xs text-[#9CA3AF]">
                                                    {item.status === 'analyzed' ? t('admin_user.status_analyzed') : item.status === 'pending' ? t('admin_user.status_pending') : item.status}
                                                </p>
                                            </div>
                                        </div>
                                        <p className="text-xs text-[#9CA3AF]">
                                            {item.created_at ? new Date(item.created_at).toLocaleDateString(locale) : '-'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-[#9CA3AF] text-center py-6">{t('admin_user.no_activity')}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
